import React from "react";
import {
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import TextField from "@mui/material/TextField";

const PARTNERED_NOTE =
    "Το κόστος υπολογίζεται κατά την ολοκλήρωση της παραγγελίας – τα έξοδα αποστολής επιβαρύνουν τον πελάτη.";

const PARTNERED_CHARGES_TITLE = "Ενδεικτικές χρεώσεις";

const PARTNERED_CHARGES_LINES = [
    "Για παραγγελίες έως 5kg: 13,00€ με ΦΠΑ.",
    "Για παραγγελίες άνω των 25kg: 35,00€ με ΦΠΑ.",
    "Εύθραυστες παλέτες (π.χ. μηχανές espresso): από 45,00€ με ΦΠΑ.",
];

function FigmaTextInput({
                            label,
                            value,
                            onChange,
                            name,
                            error,
                            helperText,
                            ...props
                        }) {
    const isDate = props.type === "date";

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: { md: 286 },
                height: 59,
            }}
        >
            <TextField
                {...props}
                name={name}
                value={value}
                onChange={onChange}
                error={error}
                helperText={helperText}
                variant="outlined"
                size="small"
                fullWidth
                label={label}
                InputLabelProps={{
                    shrink: isDate ? true : undefined,
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 16,
                        color: "#111111",
                        "&.Mui-focused": { color: "#111111" },
                    },
                }}
                InputProps={{
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 16,
                        "& .MuiInputBase-input": {
                            py: 1.1,
                            pl: 1,
                        },
                        "& fieldset": {
                            borderColor: "#111111",
                            borderWidth: 1,
                            borderRadius: "4px",
                        },
                        "&:hover fieldset": {
                            borderColor: "#111111",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#111111",
                        },
                        "& input[type='date']::-webkit-calendar-picker-indicator": {
                            cursor: "pointer",
                        },
                    },
                }}
            />
        </Box>
    );
}

export default function DeliveryMethodSection({
                                                  t,
                                                  formik,
                                                  partneredCarrierOptions = [],
                                                  recipientCarrierOptions = [],
                                              }) {
    const selectedMethod = formik.values.deliveryMethod;
    const recipientMode = formik.values.recipientCarrierMode;

    const radioSx = {
        "& .MuiSvgIcon-root": { fontSize: 20 },
        color: "#8D8D8D",
        "&.Mui-checked": { color: "#111111" },
        p: 0,
        mr: 1,
    };

    const fieldError = (name) =>
        Boolean(formik.touched[name] && formik.errors[name]);

    const fieldHelperText = (name) =>
        formik.touched[name] && formik.errors[name]
            ? formik.errors[name]
            : "";

    const nestedFieldError = (parent, field) =>
        Boolean(
            formik.touched[parent]?.[field] &&
            formik.errors[parent]?.[field]
        );

    const nestedFieldHelperText = (parent, field) =>
        formik.touched[parent]?.[field] && formik.errors[parent]?.[field]
            ? formik.errors[parent][field]
            : "";

    const handleMethodChange = (event) => {
        formik.setFieldValue("deliveryMethod", event.target.value);
    };

    const handleRecipientModeChange = (event) => {
        formik.setFieldValue("recipientCarrierMode", event.target.value);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "30px",
                maxWidth: 601,
                width: "100%",
            }}
        >
            <Typography
                sx={{
                    fontFamily: "'Zen Maru Gothic', sans-serif",
                    fontWeight: 900,
                    fontSize: 24,
                    lineHeight: "110%",
                    textTransform: "uppercase",
                    color: "#111111",
                }}
            >
                {t("Shipping way")}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "#E9E9E9",
                        borderRadius: "10px",
                        p: 2.5,
                        gap: selectedMethod === "partnered" ? 2 : 0,
                    }}
                >
                    <FormControlLabel
                        control={
                            <Radio
                                sx={radioSx}
                                checked={selectedMethod === "partnered"}
                                onChange={handleMethodChange}
                                value="partnered"
                            />
                        }
                        label={t("Cooperating transport companies")}
                        sx={{
                            m: 0,
                            "& .MuiTypography-root": {
                                fontFamily: "'Zen Maru Gothic', sans-serif",
                                fontSize: 16,
                                color: "#101010",
                            },
                        }}
                    />

                    {selectedMethod === "partnered" && (
                        <Box
                            sx={{
                                mt: 1.5,
                                bgcolor: "#F0F0F0",
                                borderRadius: "5px",
                                p: 2.5,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "'Zen Maru Gothic', sans-serif",
                                    fontSize: 16,
                                    color: "#111111",
                                }}
                            >
                                {t("Please select one of the following shipping carriers:")}
                            </Typography>

                            <RadioGroup
                                name="partneredCarrierId"
                                value={formik.values.partneredCarrierId}
                                onChange={formik.handleChange}
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    flexWrap: "wrap",
                                    gap: 6,
                                }}
                            >
                                {partneredCarrierOptions.map((choice) => (
                                    <FormControlLabel
                                        key={choice.value}
                                        value={choice.value}
                                        control={<Radio sx={radioSx} />}
                                        label={choice.label}
                                        sx={{
                                            m: 0,
                                            "& .MuiTypography-root": {
                                                fontFamily: "'Zen Maru Gothic', sans-serif",
                                                fontSize: 16,
                                                color: "#000000",
                                            },
                                        }}
                                    />
                                ))}
                            </RadioGroup>

                            {fieldHelperText("partneredCarrierId") && (
                                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                                    {fieldHelperText("partneredCarrierId")}
                                </Typography>
                            )}

                            <Typography
                                sx={{
                                    fontFamily: "'Zen Maru Gothic', sans-serif",
                                    fontSize: 14,
                                    color: "#111111",
                                }}
                            >
                                {PARTNERED_NOTE}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: "'Zen Maru Gothic', sans-serif",
                                        fontSize: 14,
                                        fontWeight: 700,
                                        textTransform: "uppercase",
                                        color: "#111111",
                                    }}
                                >
                                    {PARTNERED_CHARGES_TITLE}
                                </Typography>

                                <Box>
                                    {PARTNERED_CHARGES_LINES.map((line, index) => (
                                        <Typography
                                            key={index}
                                            sx={{
                                                fontFamily: "'Zen Maru Gothic', sans-serif",
                                                fontSize: 14,
                                                color: "#111111",
                                            }}
                                        >
                                            • {line}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "#E9E9E9",
                        borderRadius: "8px",
                        p: 2.5,
                        mt: 1,
                        gap: selectedMethod === "recipient_transport" ? 2 : 0,
                    }}
                >
                    <FormControlLabel
                        control={
                            <Radio
                                sx={radioSx}
                                checked={selectedMethod === "recipient_transport"}
                                onChange={handleMethodChange}
                                value="recipient_transport"
                            />
                        }
                        label={t("Shipping carrier chosen by the recipient")}
                        sx={{
                            m: 0,
                            "& .MuiTypography-root": {
                                fontFamily: "'Zen Maru Gothic', sans-serif",
                                fontSize: 16,
                                color: "#101010",
                            },
                        }}
                    />

                    {selectedMethod === "recipient_transport" && (
                        <Box
                            sx={{
                                mt: 1.5,
                                bgcolor: "#F0F0F0",
                                borderRadius: "5px",
                                p: 2.5,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            <RadioGroup
                                value={recipientMode}
                                onChange={handleRecipientModeChange}
                                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                            >
                                {recipientCarrierOptions.map((item) => (
                                    <FormControlLabel
                                        key={item.value}
                                        value="saved"
                                        control={
                                            <Radio
                                                sx={radioSx}
                                                checked={
                                                    recipientMode === "saved" &&
                                                    formik.values.recipientCarrierId === item.value
                                                }
                                                onChange={() => {
                                                    formik.setFieldValue("recipientCarrierMode", "saved");
                                                    formik.setFieldValue("recipientCarrierId", item.value);
                                                }}
                                            />
                                        }
                                        label={item.label}
                                        sx={{
                                            m: 0,
                                            "& .MuiTypography-root": {
                                                fontFamily: "'Zen Maru Gothic', sans-serif",
                                                fontSize: 16,
                                                color: "#000000",
                                            },
                                        }}
                                    />
                                ))}

                                <FormControlLabel
                                    value="new"
                                    control={<Radio sx={radioSx} />}
                                    label={t("Add new shipping carrier")}
                                    sx={{
                                        mt: 1,
                                        "& .MuiTypography-root": {
                                            fontFamily: "'Zen Maru Gothic', sans-serif",
                                            fontSize: 16,
                                            color: "#000000",
                                        },
                                    }}
                                />
                            </RadioGroup>

                            {recipientMode === "saved" && fieldHelperText("recipientCarrierId") && (
                                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                                    {fieldHelperText("recipientCarrierId")}
                                </Typography>
                            )}

                            {recipientMode === "new" && (
                                <>
                                    <Typography
                                        sx={{
                                            mt: 2,
                                            fontFamily: "'Zen Maru Gothic', sans-serif",
                                            fontSize: 16,
                                            color: "#111111",
                                        }}
                                    >
                                        {t("Please enter the contact details of the shipping carrier you selected:")}
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1.5,
                                            width: { xs: "100%", sm: "100%", md: 286 },
                                        }}
                                    >
                                        <FigmaTextInput
                                            label={t("Carrier Name")}
                                            name="recipientCarrierForm.companyName"
                                            value={formik.values.recipientCarrierForm.companyName}
                                            onChange={formik.handleChange}
                                            error={nestedFieldError("recipientCarrierForm", "companyName")}
                                            helperText={nestedFieldHelperText("recipientCarrierForm", "companyName")}
                                        />
                                        <FigmaTextInput
                                            label={t("Contract")}
                                            name="recipientCarrierForm.contactPerson"
                                            value={formik.values.recipientCarrierForm.contactPerson}
                                            onChange={formik.handleChange}
                                            error={nestedFieldError("recipientCarrierForm", "contactPerson")}
                                            helperText={nestedFieldHelperText("recipientCarrierForm", "contactPerson")}
                                        />
                                        <FigmaTextInput
                                            label={t("Delivery Date")}
                                            type="date"
                                            name="recipientCarrierForm.pickupDate"
                                            value={formik.values.recipientCarrierForm.pickupDate}
                                            onChange={formik.handleChange}
                                            error={nestedFieldError("recipientCarrierForm", "pickupDate")}
                                            helperText={nestedFieldHelperText("recipientCarrierForm", "pickupDate")}
                                        />
                                        <FigmaTextInput
                                            label={t("Phone")}
                                            name="recipientCarrierForm.phone"
                                            value={formik.values.recipientCarrierForm.phone}
                                            onChange={formik.handleChange}
                                            error={nestedFieldError("recipientCarrierForm", "phone")}
                                            helperText={nestedFieldHelperText("recipientCarrierForm", "phone")}
                                        />
                                        <FigmaTextInput
                                            label={t("E-mail")}
                                            name="recipientCarrierForm.email"
                                            value={formik.values.recipientCarrierForm.email}
                                            onChange={formik.handleChange}
                                            error={nestedFieldError("recipientCarrierForm", "email")}
                                            helperText={nestedFieldHelperText("recipientCarrierForm", "email")}
                                        />
                                        <FigmaTextInput
                                            label={t("Address")}
                                            name="recipientCarrierForm.address"
                                            value={formik.values.recipientCarrierForm.address}
                                            onChange={formik.handleChange}
                                            error={nestedFieldError("recipientCarrierForm", "address")}
                                            helperText={nestedFieldHelperText("recipientCarrierForm", "address")}
                                        />
                                        <FigmaTextInput
                                            label={t("City")}
                                            name="recipientCarrierForm.city"
                                            value={formik.values.recipientCarrierForm.city}
                                            onChange={formik.handleChange}
                                            error={nestedFieldError("recipientCarrierForm", "city")}
                                            helperText={nestedFieldHelperText("recipientCarrierForm", "city")}
                                        />
                                        <FigmaTextInput
                                            label={t("Zip Code")}
                                            name="recipientCarrierForm.zipCode"
                                            value={formik.values.recipientCarrierForm.zipCode}
                                            onChange={formik.handleChange}
                                            error={nestedFieldError("recipientCarrierForm", "zipCode")}
                                            helperText={nestedFieldHelperText("recipientCarrierForm", "zipCode")}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1.25,
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formik.values.saveRecipientCarrier}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(
                                                            "saveRecipientCarrier",
                                                            e.target.checked
                                                        )
                                                    }
                                                    sx={{
                                                        p: 0,
                                                        mr: 1.25,
                                                        "& .MuiSvgIcon-root": {
                                                            fontSize: 18,
                                                        },
                                                    }}
                                                />
                                            }
                                            label={t("Save this carrier to my account")}
                                            sx={{
                                                m: 0,
                                                "& .MuiTypography-root": {
                                                    fontFamily: "'Roboto Condensed', sans-serif",
                                                    fontSize: 14,
                                                    color: "#111111",
                                                },
                                            }}
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formik.values.acceptRecipientTerms}
                                onChange={(e) =>
                                    formik.setFieldValue(
                                        "acceptRecipientTerms",
                                        e.target.checked
                                    )
                                }
                                sx={{
                                    p: 0,
                                    mr: 1.25,
                                    "& .MuiSvgIcon-root": {
                                        fontSize: 18,
                                    },
                                }}
                            />
                        }
                        label={t("I agree to the terms of service. (Read the terms of service)")}
                        sx={{
                            m: 0,
                            "& .MuiTypography-root": {
                                fontFamily: "'Roboto Condensed', sans-serif",
                                fontSize: 14,
                                color: "#111111",
                            },
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "#E9E9E9",
                        borderRadius: "10px",
                        p: 2.2,
                        mt: 1,
                    }}
                >
                    <FormControlLabel
                        control={
                            <Radio
                                sx={radioSx}
                                checked={selectedMethod === "courier"}
                                onChange={handleMethodChange}
                                value="courier"
                            />
                        }
                        label={t("Courier")}
                        sx={{
                            m: 0,
                            "& .MuiTypography-root": {
                                fontFamily: "'Zen Maru Gothic', sans-serif",
                                fontSize: 16,
                                color: "#101010",
                            },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}